/**
 * The water a man pushes through (02 §4). Reported in play: "wading through water should look
 * different too."
 *
 * It already WAS different, and invisibly so: a wader stands on the carved bed 0.6 m under the
 * surface plane, moves at `HAZARDS.wadeSpeed`, burns stamina and makes `HAZARDS.wadeNoise` - four
 * rules, none of them on screen. The surface mesh does cut him off at the waterline (measured: it
 * takes exactly the bottom 0.6 m of him), but nothing else says why, so a man standing IN a river
 * read as a short man standing ON one.
 *
 * So the water answers him. A COLLAR of broken water rides the surface round his legs wherever he
 * is standing in it - that is the waterline, and it is drawn whether he moves or not. Rings shed
 * off it while he pushes through, widening and thinning behind him. Standing still spreads nothing,
 * which is itself the tell: the noise rule rewards exactly that.
 *
 * The gate is the SIM's own `waterAt` - the very call that makes him slow, tired and loud - so the
 * picture can never promise wading where the rules do not charge for it, which is the whole of
 * ROUND5's complaint. Nothing here blends: the rings thin on the dither screen, like the smoke and
 * the searchlight beams (05 §8.1).
 */

import { type BufferGeometry, type Material, Mesh, type Scene, type Texture, Vector3 } from 'three';
import { rangerOnFoot } from '../../sim/hazards/terrain';
import { waterAt } from '../../sim/terrainQueries';
import type { MissionDef, World } from '../../sim/types';
import { MeshBuilder } from '../kit/MeshBuilder';
import { createDitherMaterial, ditherStep } from '../materials/dither';
import { createToonMaterial, OUTLINE } from '../materials/toon';
import type { Palette } from '../palette';
import { RIVER_LEVEL } from './river';

const TAU = Math.PI * 2;

/**
 * Height the broken water is drawn at: clear of BOTH surface planes - a barrier's `RIVER_LEVEL` and
 * a pond's -0.28 (terrainFromDef.ts) - so it can never z-fight the water it sits on. Five
 * centimetres is under a tenth of a pixel of lift at the ground zoom.
 */
export const WASH_Y: number = RIVER_LEVEL + 0.05;

/** Rings alive at once: enough for a steady wake at walking pace, and cheap. */
const RINGS = 6;
/** Seconds between one ring and the next while he pushes through. */
const EVERY = 0.3;
/** Seconds a ring takes to spread and go. */
const LIFE = 1.2;
const START_RADIUS = 0.55;
const END_RADIUS = 2.3;
/** Share of its pixels a fresh ring keeps on the dither screen; it thins from here to nothing. */
const RING_DENSE = 0.85;

/** Outer radius of the collar round a man standing in it: a little wider than his shoulders. */
const COLLAR = 0.62;
/** What pushing through adds to it - the wash he shoulders along in front of him. */
const COLLAR_PUSH = 0.26;
/** The share of the collar that is there in ankle-deep water; the rest comes with the depth. */
const COLLAR_FLOOR = 0.32;
/** Water this deep breaks round him at full width - a ford's 0.12 m only ever laps his boots. */
const SHIN = 0.5;
/** Shallower than this and he is not in it at all: no collar, whatever a bank's slack says. */
const MIN_DEPTH = 0.05;
/** Sim ticks per step of the collar's wobble: the river's own 5 Hz, hand-drawn frames not a crawl. */
const WOBBLE_TICKS = 12;
const WOBBLE = 0.09;

/** Flat annulus on y = 0 at unit outer radius, facing up. `inner` is a share of the outer radius. */
function annulus(palette: Palette, inner: number, sides = 16): MeshBuilder {
  const mb = new MeshBuilder();
  const color = palette.color('water.3');
  const at = (a: number, r: number): Vector3 => new Vector3(Math.sin(a) * r, 0, Math.cos(a) * r);
  for (let i = 0; i < sides; i++) {
    const a0 = (i / sides) * TAU;
    const a1 = ((i + 1) / sides) * TAU;
    // Wound so the face looks UP: the material is single-sided and the camera is overhead.
    mb.quad(at(a0, inner), at(a0, 1), at(a1, 1), at(a1, inner), color);
  }
  return mb;
}

interface Ring {
  mesh: Mesh;
  /** Its own share of kept pixels: six rings on one shared object would thin together and flicker. */
  coverage: { value: number };
  /** Seconds since it was shed; past `LIFE` it is spent and free to be used again. */
  age: number;
}

export class Wake {
  private readonly rings: Ring[] = [];
  private readonly collar: Mesh;
  private readonly geometries: BufferGeometry[] = [];
  private readonly materials: Material[] = [];
  private since = EVERY;
  private next = 0;

  constructor(
    scene: Scene,
    palette: Palette,
    gradient: Texture,
    private readonly def: MissionDef,
    /** Where his feet are (`StaticWorld.groundY`): how deep he stands decides how much water breaks. */
    private readonly groundY: (x: number, z: number) => number,
  ) {
    // Rings, not discs. The first attempt drew a filled disc and called it an annulus; opaque, it
    // reads as an ice floe following him about. The collar is a tighter band than the shed rings.
    const collarGeometry = annulus(palette, 0.55).build();
    const ringGeometry = annulus(palette, 0.72).build();
    this.geometries.push(collarGeometry, ringGeometry);

    // NOT `material.clone()`. `Material.copy` does not carry `onBeforeCompile`, so a clone of a toon
    // material never gets `patchMrt` - and against this pipeline's two-attachment target the driver
    // throws EVERY draw out ("active draw buffers with missing fragment shader outputs"). That, and
    // nothing subtler, is why the first wake was invisible: it was never drawn at all.
    const collarMaterial = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.none });
    this.materials.push(collarMaterial);
    this.collar = new Mesh(collarGeometry, collarMaterial);
    this.collar.name = 'wake-collar';
    this.collar.visible = false;
    scene.add(this.collar);

    for (let i = 0; i < RINGS; i++) {
      const coverage = { value: RING_DENSE };
      const material = createDitherMaterial({ gradientMap: gradient, coverage });
      this.materials.push(material);
      const mesh = new Mesh(ringGeometry, material);
      mesh.name = 'wake-ring';
      mesh.visible = false;
      scene.add(mesh);
      this.rings.push({ mesh, coverage, age: LIFE });
    }
  }

  /** Each ring on screen: its outer radius, metres, and the share of its pixels it still keeps. */
  get liveRings(): Array<{ radius: number; coverage: number }> {
    return this.rings
      .filter((r) => r.mesh.visible)
      .map((r) => ({ radius: r.mesh.scale.x, coverage: r.coverage.value }));
  }

  /** Rings currently on screen (for tests and the debug overlay). */
  get ringsVisible(): number {
    return this.liveRings.length;
  }

  /** Outer radius of the collar this frame, metres; 0 when he is not standing in water. */
  get collarRadius(): number {
    return this.collar.visible ? this.collar.scale.x : 0;
  }

  update(world: World, dt: number, alpha: number): void {
    const r = world.ranger;
    const x = r.prevX + (r.x - r.prevX) * alpha;
    const z = r.prevZ + (r.z - r.prevZ) * alpha;
    // The sim's own wading test, not a look-alike: exactly where this is true he is slowed by
    // `HAZARDS.wadeSpeed`, charged `wadeFatiguePerSecond` and heard at `wadeNoise`.
    const wading = rangerOnFoot(world) && waterAt(this.def, x, z);
    const depth = wading ? RIVER_LEVEL - this.groundY(x, z) : 0;
    this.wearCollar(world, x, z, depth, r.moving);
    // Only a man MOVING through it spreads any: wading is the slow, loud way across, and standing
    // still in it is the quiet one.
    this.shed(depth > MIN_DEPTH && r.moving, x, z, dt);
    this.spread(dt);
  }

  /** The waterline itself: a band of broken water round his legs, wider the deeper he stands. */
  private wearCollar(world: World, x: number, z: number, depth: number, moving: boolean): void {
    if (depth <= MIN_DEPTH) {
      this.collar.visible = false;
      return;
    }
    const wade = Math.min(1, depth / SHIN);
    // It breathes on the river's own beat rather than every frame, so it belongs to the water.
    const beat = Math.floor(world.tick / WOBBLE_TICKS) % 2 === 0 ? 1 + WOBBLE : 1 - WOBBLE;
    const radius = (COLLAR * (COLLAR_FLOOR + (1 - COLLAR_FLOOR) * wade) + (moving ? COLLAR_PUSH : 0)) * beat;
    this.collar.position.set(x, WASH_Y, z);
    this.collar.scale.set(radius, 1, radius);
    this.collar.visible = true;
  }

  private shed(on: boolean, x: number, z: number, dt: number): void {
    this.since += dt;
    if (!on || this.since < EVERY) return;
    this.since = 0;
    const ring = this.rings[this.next];
    this.next = (this.next + 1) % this.rings.length;
    if (!ring) return;
    ring.age = 0;
    ring.mesh.position.set(x, WASH_Y, z);
  }

  private spread(dt: number): void {
    for (const ring of this.rings) {
      if (ring.age >= LIFE) {
        ring.mesh.visible = false;
        continue;
      }
      ring.age = Math.min(LIFE, ring.age + dt);
      const t = ring.age / LIFE;
      const radius = START_RADIUS + (END_RADIUS - START_RADIUS) * t;
      ring.mesh.scale.set(radius, 1, radius);
      ring.mesh.visible = true;
      // It thins as it widens, the way a real ring does - in the dither's sixteen steps, so the
      // fade goes in visible notches rather than crawling.
      ring.coverage.value = ditherStep(RING_DENSE * (1 - t));
    }
  }

  dispose(): void {
    for (const mesh of [this.collar, ...this.rings.map((r) => r.mesh)]) mesh.removeFromParent();
    for (const material of this.materials) material.dispose();
    for (const geometry of this.geometries) geometry.dispose();
    this.rings.length = 0;
    this.materials.length = 0;
    this.geometries.length = 0;
  }
}
